<template>
    <div class="pay-in__body px-4 pt-4 pb-5">
        <label class="mb-3 pay-in__review-label">Review your payments</label>

        <pay-in-summary-card
            v-for="(payment, index) in payments"
            :key="index"
            :payment="payment"
            :index="index"
            :isPartial="isPartial"
            :amount="payment.paidValue"
            :willCreateInvoiceForAllPayments="willCreateInvoiceForAllPayments"
            :isFromCreatePayin="isFromCreatePayin"
        />

        <!-- Total paid / Remaining balance -->
        <div class="pay-in__summary-totals mb-3">
            <div class="pay-in__summary-row pay-in__summary-row--total px-3">
                <span class="font-weight-600 fs-small darkest">Total paid</span>
                <span class="font-weight-600 fs-large pay-in__green">{{ formatPrice(totalPaid) }}€</span>
            </div>
            <div class="pay-in__summary-row px-3 py-1" v-if="finalChange > 0">
                <span class="darkest fs-small">Change</span>
                <span class="darkest fs-large font-weight-600">{{ formatPrice(finalChange) }}€</span>
            </div>
            <div class="pay-in__summary-row px-3 py-1">
                <span class="darkest fs-small">Remaining balance</span>
                <span class="darkest fs-large font-weight-600">{{ formatPrice(remainingBalance) }}€</span>
            </div>
        </div>

        <!-- Invoice dropdown options -->
        <template v-if="shouldShowInvoiceDropdown">
            <div class="pay-in__invoice-dropdown mt-4">
                <label class="pay-in__invoice-dropdown-label mb-2 d-block">Review your invoice options</label>
                <v-select
                    class="pay-in__invoice-select"
                    label="name"
                    :options="invoiceModeOptions"
                    v-model="localInvoiceMode"
                    @input="onInvoiceModeChanged">
                </v-select>
            </div>
        </template>
    </div>
</template>

<script>
import { reservationsMixin } from '@/mixins/reservations'
import { mapGetters } from 'vuex'
import PayInSummaryCard from '@/tools/modal/ReservationPayIn/PayInSummaryCard'
import extendedVSelect from '@/extends/vue-select'
import { PAYMENT_SCOPE } from '@/tools/modal/ReservationPayIn/payInHelpers'
const INVOICE_MODE_OPTIONS = [
    { name: 'Do not create invoice', value: 'None' },
    { name: 'Create one invoice for all payments', value: 'Combined' },
    { name: 'Create one invoice per payment', value: 'PerPayment' },
]

export default {
    mixins: [reservationsMixin],
    components: {
        PayInSummaryCard,
        vSelect: extendedVSelect,
    },
    props: {
        form: {
            type: Object,
            required: true,
        },
        selectedScope: {
            type: String,
            required: true,
        },
        partialCreatedPayments: {
            type: Array,
            default: () => [],
        },
        amountDue: {
            type: Number,
            required: true,
        },
        invoiceMode: {
            type: String,
            default: null,
        },
        isFromCreatePayin: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localInvoiceMode: INVOICE_MODE_OPTIONS.find(o => o.value === (this.invoiceMode || 'None')) || INVOICE_MODE_OPTIONS[0],
            invoiceModeOptions: INVOICE_MODE_OPTIONS,
        }
    },
    watch: {
        invoiceMode(newVal) {
            this.localInvoiceMode = INVOICE_MODE_OPTIONS.find(o => o.value === (newVal || 'None')) || INVOICE_MODE_OPTIONS[0]
        },
    },
    methods: {
        onInvoiceModeChanged(option) {
            this.$emit('update:invoiceMode', option ? option.value : null)
        },
    },
    computed: {
        ...mapGetters([
            'getPaymentTypeListAll'
        ]),
        isPartial() {
            return this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS
        },
        willCreateInvoiceForAllPayments() {
            return ['Combined', 'PerPayment'].includes(this.localInvoiceMode && this.localInvoiceMode.value)
        },
        paymentTypeMap() {
            const allPaymentTypes = this.getPaymentTypeListAll || []
            const map = {}
            allPaymentTypes.forEach(type => {
                map[type.id] = type.description
            })
            return map
        },
        payments() {
            if (this.isPartial) {
                return this.partialCreatedPayments.map(payment => {
                    const items = payment.items || []
                    const itemsTotal = items.reduce((sum, item) => sum + (item.total || 0), 0)
                    const paymentMethodsTotal = payment.paymentMethods.reduce((sum, method) => sum + (parseFloat(method.amountReceived) || 0), 0)
                    const change = Math.max(0, paymentMethodsTotal - itemsTotal)
                    return {
                        reference: payment.reference,
                        itemsLabel: payment.itemsLabel,
                        items,
                        paymentMethods: payment.paymentMethods,
                        change,
                        paidValue: Math.max(0, paymentMethodsTotal - change),
                        createInvoice: payment.createInvoice || false,
                        hasBillingDetails: payment.hasBillingDetails || false,
                        billingName: payment.billingName || '',
                    }
                })
            }
            const totalReceived = this.form.payments.reduce((sum, payment) => sum + (parseFloat(payment.amountReceived) || 0), 0)
            const totalToPay = this.form.payments.reduce((sum, payment) => sum + (parseFloat(payment.toPay) || parseFloat(payment.amountReceived) || 0), 0)
            const change = Math.max(0, totalReceived - totalToPay)
            return [{
                itemsLabel: null,
                items: [],
                paymentMethods: this.form.payments.map(payment => ({
                    typeName: this.paymentTypeMap[payment.type] || 'Unknown',
                    amountReceived: parseFloat(payment.amountReceived) || 0,
                    toPay: parseFloat(payment.toPay) || 0,
                })),
                change,
                paidValue: Math.max(0, totalReceived - change),
                createInvoice: this.form.createInvoice,
                hasBillingDetails: this.form.addBillingDetails,
                billingName: this.form.billingDetails.businessName || '',
            }]
        },
        totalPaid() {
            const totalReceived = this.payments.reduce((sum, payment) => {
                return sum + payment.paymentMethods.reduce((methodSum, paymentMethod) => methodSum + (parseFloat(paymentMethod.amountReceived) || 0), 0)
            }, 0)
            return Math.max(0, totalReceived - this.finalChange)
        },
        totalFullChange() {
            const totalReceived = this.payments.reduce((sum, payment) => {
                return sum + payment.paymentMethods.reduce((methodSum, paymentMethod) => methodSum + (parseFloat(paymentMethod.amountReceived) || 0), 0)
            }, 0)
            const totalToPay = this.payments.reduce((sum, payment) => {
                return sum + payment.paymentMethods.reduce((methodSum, paymentMethod) => methodSum + (parseFloat(paymentMethod.toPay) || paymentMethod.amountReceived), 0)
            }, 0)
            return Math.max(0, totalReceived - totalToPay)
        },
        totalPartialChange() {
            return this.partialCreatedPayments.reduce((sum, payment) => {
                const itemsTotal = payment.items.reduce((itemSum, item) => itemSum + (item.total || 0), 0)
                
                const paymentMethodsTotal = payment.paymentMethods.reduce((methodSum, method) => methodSum + (parseFloat(method.amountReceived) || 0), 0)
                
                const paymentChange = Math.max(0, paymentMethodsTotal - itemsTotal)
                
                return sum + paymentChange
            }, 0)
        },
        remainingBalance() {
            return Math.max(0, this.amountDue - this.totalPaid)
        },
        allPaymentsHaveInvoice() {
            return this.payments.length > 0 && this.payments.every(payment => payment.createInvoice)
        },
        noPaymentsHaveInvoice() {
            return this.payments.length > 0 && this.payments.every(payment => !payment.createInvoice)
        },
        somePaymentsHaveInvoice() {
            return this.payments.some(payment => payment.createInvoice) && !this.allPaymentsHaveInvoice
        },
        shouldShowInvoiceDropdown() {
            const isSinglePartialPayment = this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS && this.partialCreatedPayments.length === 1
            return (this.noPaymentsHaveInvoice || this.somePaymentsHaveInvoice) && this.selectedScope !== PAYMENT_SCOPE.FULL_BOOKING && !isSinglePartialPayment
        },
        finalChange() {
            return this.isPartial ? this.totalPartialChange : this.totalFullChange
        },
    },
    mounted() {
        if (this.shouldShowInvoiceDropdown && !this.invoiceMode) {
            this.$emit('update:invoiceMode', 'None')
        }
    },
}
</script>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';

    .pay-in__body {
        min-height: 11.25rem;
    }


    .pay-in__review-label {
        display: block;
        color: $darkest;
        font-size: $font-size-small;
    }

    .pay-in__summary-row--total {
        padding: 0.5rem 0;
        background: $lighter;
    }

    .pay-in__summary-totals {
        border: 1px solid $color-3;
        border-radius: 0.25rem;
        overflow: hidden;
    }

    .pay-in__green {
        color: $color-3;
    }

    .pay-in__invoice-dropdown-label {
        color: $darkest;
        font-size: $font-size-small;
        font-weight: 600;
    }

    .pay-in__invoice-select {
        width: 300px;
        max-width: 100%;
    }

</style>

<style lang="scss">
    @import '../../../assets/scss/_variables.scss';

    .pay-in-modal .pay-in__invoice-radio label {
        font-family: 'Open Sans Regular', Helvetica, Arial, sans-serif !important;
        font-size: $font-size-small;
        font-weight: normal !important;
        color: $darkest;
    }

    .pay-in-modal .pay-in__invoice-radio .input-box {
        width: 11px !important;
        height: 11px !important;
    }

    .pay-in-modal .pay-in__invoice-radio .input-box-circle {
        width: 5px !important;
        height: 5px !important;
        margin: 2px !important;
    }
</style>


// WEBPACK FOOTER //
// Step3Summary.vue?1b55e96c